import { Dayjs } from 'dayjs';
import {
  AlertType,
  DataLakeQuery,
  DataLakeQuerySortDirection,
  DataLakeQueryVisualizationResultsColumn,
  Query,
  SearchQueryOperator,
} from 'Generated/schema';
import { RelativeTimeUnits } from 'pounceV2';
import { FilterKeyType } from '../types';
import type { CrossLogSearchFormValues } from './CrossLogSearch/types';

/**
 * Interface for URL search parameters for use in Query Builder
 */
export interface QueryBuilderUrlParams {
  startTime?: string;
  endTime?: string;
  queryId?: string;
  savedQueryId?: string;
  execute?: boolean;
  logType?: string;
  sourceId?: string;
  alertId?: string;
  alertType?: AlertType;
  view?: ResultsViewEnum;
  usePantherFlow?: boolean;
  pfQuery?: string;
}

export type QueryFilter = {
  key: string; // Diverges from Filter type
  operator: SearchQueryOperator;
  value?: string | string[] | number;
};

export type OrsFilter = {
  ors: Array<{ ands: QueryFilter[] }>;
};

export type SimpleSearchQueryFilter = QueryFilter | { ands: QueryFilter[] } | OrsFilter;

export type Metadata = {
  relativeTime: string; // ISO 8601 Duration (preset or custom single-unit), or PAllTime
  startTime: string; // ISO 8601
  endTime: string; // ISO 8601
  allTime?: boolean;
  databases: string[];
  tables: string[];
  limit: number;
  tags?: Record<string, any>;
  sortDirection?: DataLakeQuerySortDirection;
  language: string;
  // Present for simple search searches
  simpleSearchQueryFilter?: SimpleSearchQueryFilter;
  // Present for PantherFlow searches
  query?: string;
  relativeNumberVal?: number;
  relativeTimeUnit?: RelativeTimeUnits;
};

export type Operator = {
  name: string;
  group: string;
  type?: string;
  value: SearchQueryOperator;
  initialFieldValue?: string | string[] | number[] | boolean;
};

export interface ColumnProperty {
  id: string;
  path: string;
  type: string;
  table?: string;
  description?: string;
  displayName: string;
  isPantherField: boolean;
  isUserInput?: boolean;
  isUserAutoDetectInput?: boolean;
}

export interface ColumnPropertyWithLogType extends ColumnProperty {
  logType: string;
}

export type Filter = {
  key?: ColumnProperty | string;
  keyType: FilterKeyType;
  operator?: Operator;
  value?: string | string[] | number | number[] | boolean;
  uuid: string;
};

export type Filters = {
  ors: Array<{
    uuid: string;
    ands: Filter[];
  }>;
};

export type SimpleQueryFormValues = {
  startTime: Dayjs;
  endTime: Dayjs;
  database: string;
  table?: string;
  filters?: Filters;
  pantherFlowInput?: string;
  usePantherFlow: boolean;
  language?: string;
  limit: number;
  sortDirection?: DataLakeQuerySortDirection;
  relativeNumberVal?: number;
  relativeTimeUnit?: RelativeTimeUnits;
};

export type UnifiedFormValues = SimpleQueryFormValues | CrossLogSearchFormValues;

export type MinimalQueryInfo = Pick<DataLakeQuery, 'id' | 'sql' | 'kind' | 'metadata'>;

export enum ResultsViewEnum {
  RESULTS = 'results',
  VISUALIZATIONS = 'visualizations',
}

export enum SearchResultsTableViewEnum {
  COMPACT = 'compact',
  DETAILED = 'detailed',
}

export interface ResultsUrlParams {
  view: ResultsViewEnum;
  tableView: SearchResultsTableViewEnum;
}

export type VisualizationAxes = {
  x: DataLakeQueryVisualizationResultsColumn;
  y: DataLakeQueryVisualizationResultsColumn;
};

export type VisualizationData = {
  data: Pick<Query, 'uberSearchVisualization'>;
};

export type { CrossLogSearchFormValues } from './CrossLogSearch/types';
